package io.horion.auth.utils

import io.horion.auth.api.models.domain.UserProfile
import kotlinx.browser.window
import kotlinx.serialization.json.Json

object LocalStorageManager {

    private const val KEY_ACCOUNTS_MAP = "user_accounts_map"
    private const val KEY_ACTIVE_USER_UUID = "active_user_uuid"

    private val json = Json { ignoreUnknownKeys = true }

    // --- ACCÈS À TOUS LES COMPTES ---
    fun getAllAccounts(): Map<String, UserProfile> {
        val jsonString = window.localStorage.getItem(KEY_ACCOUNTS_MAP) ?: return emptyMap()
        return runCatching {
            json.decodeFromString<Map<String, UserProfile>>(jsonString)
        }.getOrDefault(emptyMap())
    }

    // --- ENREGISTREMENT / MISE À JOUR D'UN COMPTE ---
    fun saveUserProfile(user: UserProfile) {
        if (user.id.isBlank()) return

        val currentAccounts = getAllAccounts().toMutableMap()
        currentAccounts[user.id] = user

        window.localStorage.setItem(KEY_ACCOUNTS_MAP, json.encodeToString(currentAccounts))
        setActiveUserUuid(user.id) // Le dernier connecté devient le compte actif
    }

    // --- COMPTE ACTIF ---
    fun getActiveUserUuid(): String? {
        return window.localStorage.getItem(KEY_ACTIVE_USER_UUID)
    }

    fun setActiveUserUuid(userUuid: String) {
        window.localStorage.setItem(KEY_ACTIVE_USER_UUID, userUuid)
    }

    fun getUserProfile(): UserProfile? {
        val activeUuid = getActiveUserUuid() ?: return null
        return getAllAccounts()[activeUuid]
    }

    fun getUserProfileByUuid(userUuid: String): UserProfile? {
        return getAllAccounts()[userUuid]
    }

    // --- MISE À JOUR CIBLÉE 2FA ---
    fun update2FaProfile(
        isTwoFactorEnabled: Boolean,
        newEmail: String? = null,
        newPhone: String? = null
    ) {
        val currentProfile = getUserProfile() ?: return

        val updatedProfile = currentProfile.copy(
            isTwoFactorEnabled = isTwoFactorEnabled,
            email = newEmail ?: currentProfile.email,
            phone = newPhone ?: currentProfile.phone
        )

        saveUserProfile(updatedProfile)
    }

    fun removeAccount(userUuid: String) {
        val currentAccounts = getAllAccounts().toMutableMap()
        currentAccounts.remove(userUuid)

        window.localStorage.setItem(KEY_ACCOUNTS_MAP, json.encodeToString(currentAccounts))

        // Si on a supprimé le compte actif, on bascule sur un autre compte s'il en reste
        if (getActiveUserUuid() == userUuid) {
            val nextActive = currentAccounts.keys.firstOrNull()
            if (nextActive != null) {
                setActiveUserUuid(nextActive)
            } else {
                window.localStorage.removeItem(KEY_ACTIVE_USER_UUID)
            }
        }
    }

    fun clearAllSessions() {
        window.localStorage.removeItem(KEY_ACCOUNTS_MAP)
        window.localStorage.removeItem(KEY_ACTIVE_USER_UUID)
    }
}